import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
// Deliberately aliased: the axios instance from the context is called `axios` below, and
// CancelToken lives on the module rather than on that instance.
import axiosLib, { CancelTokenSource } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IOptionItem, Select } from '../components/Form/Select/Select';
import datePicker from '../images/date-picker.svg';
import { ITableRow, TableSimple } from '../components/Table/TableSimple';
import { TablePager } from '../components/Table/TablePager';
import { useAxiosSwagger } from '../hooks/useAxiosSwagger';
import { useIdentity } from '../auth/useIdentity';
import {
    EnvironmentClient,
    EnvironmentResponseDto,
    ObjectType,
    QueueLogClient,
    QueueLogMessageDto,
    ScenarioRunClient,
    ScenarioRunSummary,
} from '../api/api';
import { ScenarioTestsButton } from '../components/ScenarioRun/ScenarioTestsButton';
import {
    Breadcrumbs,
    environmentCrumbs,
} from '../components/Breadcrumbs/Breadcrumbs';
import { scenarioTestsPath } from '../utils/paths';
import { loadingStateType } from './dashboard';
import { useRequeue } from '../hooks/useRequeue';
import { useQueueMonitor } from '../hooks/useQueueMonitor';
import { useExactConnection } from '../hooks/useExactConnection';
import { useHealth } from '../hooks/useHealth';
import { useWorkerActivity } from '../hooks/useWorkerActivity';
import { useTrace } from '../hooks/useTrace';
import { useReplay } from '../hooks/useReplay';
import { useReprocess } from '../hooks/useReprocess';
import { Finding, FindingAction } from '../api/health';
import { HealthPanel } from '../components/Health/HealthPanel';
import { WorkerActivityPanel } from '../components/Health/WorkerActivityPanel';
import { CollapsibleSection } from '../components/Section/CollapsibleSection';
import { QueueMonitorPanel } from '../components/Queues/QueueMonitorPanel';
import { ExactConnectionPanel } from '../components/Exact/ExactConnectionPanel';
import { RequeueBar } from '../components/Requeue/RequeueBar';
import { RequeueResult } from '../components/Requeue/RequeueResult';
import { TrackingPanel } from '../components/Requeue/TrackingPanel';
import { TracePanel } from '../components/Trace/TracePanel';
import { DossierPanel } from '../components/Dossier/DossierPanel';
import {
    ErrorSummary,
    codeOf,
    isSettled,
} from '../components/Interpretation/ErrorSummary';
import { InterpretationNote } from '../components/Interpretation/InterpretationNote';
import { useDossier } from '../hooks/useDossier';
import { useDailyReport } from '../hooks/useDailyReport';
import { useBusy } from '../hooks/useBusy';
import { DailyReportPanel } from '../components/Report/DailyReportPanel';
import { ReplayPanel } from '../components/Replay/ReplayPanel';
import { TraceSubject } from '../api/trace';
import { REPLAY_KIND_OF } from '../api/replay';
import {
    describeError,
    RequeueItem,
    SyncStatusSummary,
    statusKey,
} from '../api/requeue';

/**
 * How many rows we put in the DOM at once. Thousands of rows with a checkbox and a
 * tooltip per cell make the page unusable: every selection would rebuild the entire table
 * fragment. Selecting and requeueing do work across the full list, not just the visible
 * page.
 */
const PAGE_SIZE_OPTIONS = [50, 100, 250, 500];
const DEFAULT_PAGE_SIZE = 100;

/**
 * What the number above the table does and does not count.
 *
 * The server hands out one row per object and drops whatever reached Exact in the
 * meantime, so a shrinking count is the list working as intended rather than rows going
 * missing. That is worth saying out loud, once, next to the number itself.
 */
const LIST_SCOPE_HINT =
    'Eén regel per object, alleen de laatste poging, en alleen objecten die nu nog geen ' +
    'Exact-id hebben. Wat inmiddels goed staat verdwijnt uit deze lijst.';

/**
 * Whether the diagnosis panel is drawn.
 *
 * Off at request, and a constant rather than a deleted block because "for now" is what was
 * asked. Nothing about the reasoning is lost while it is off: the findings still travel to
 * the queue badges, which is where they name an app or a queue somebody can act on.
 */
const SHOW_HEALTH = false;

/** The foldable sections of this page. */
type SectionKey = 'exact' | 'queues' | 'tracking' | 'trace' | 'replay' | 'logs';

/**
 * Which sections start unfolded.
 *
 * Only the error log. Six panels drawn out in full is a page you scroll past rather than
 * read, and the log is what people come here for; the rest opens when a question about it
 * comes up - or on its own, when something on the page has to put the reader in front of
 * what is inside it.
 */
const INITIAL_OPEN: SectionKey[] = ['logs'];

/**
 * Everything that determines a single list of error rows.
 *
 * Customer and environment are part of it because they decide which rows you get just as
 * much as the period does: switching environments only changes the url, this screen stays
 * mounted. Without those two in the query the effect below would have to watch the route,
 * and every switch would start a round it immediately aborts itself.
 *
 * The screen keeps two of these: `applied` belongs to the rows you see right now,
 * `pending` to what the user just picked and is still on its way. The controls bind to
 * `pending ?? applied`, which makes cancelling nothing more than throwing `pending` away -
 * the filter snaps back to the state that matches the visible data on its own.
 */
type LogQuery = {
    customer: string;
    environment: string;
    startDate: Date | null;
    endDate: Date | null;
    objectType: ObjectType | '';
};

/** A query you are actually allowed to fetch with: both dates are settled. */
type ReadyQuery = LogQuery & { startDate: Date; endDate: Date };

const createInitialQuery = (
    customer?: string,
    environment?: string
): LogQuery => {
    const start = new Date();
    start.setDate(start.getDate() - 7);

    return {
        customer: customer ?? '',
        environment: environment ?? '',
        startDate: start,
        endDate: new Date(),
        objectType: '',
    };
};

/**
 * A range picker returns [start, null] after the first click: the user is still picking.
 * Without this check the screen fired a request right there that the client rejects with
 * "The parameter 'lastDate' must be defined" - an error in the console and a wasted round
 * trip to Azure.
 */
const isComplete = (query: LogQuery | null): query is ReadyQuery =>
    !!query && !!query.startDate && !!query.endDate;

const dayLabel = (date: Date | null) =>
    date
        ? date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
        : '';

/**
 * The columns that only exist when they hold something.
 *
 * Parent and Related are either filled or empty depending on the object type; a column
 * that is empty for every visible row only costs width the message column could put to
 * good use.
 */
const optionalColumn = (
    rows: QueueLogMessageDto[],
    pick: (row: QueueLogMessageDto) => string | undefined
) => rows.some((row) => !!pick(row));

/**
 * The "In Exact" column in three states.
 *
 * A red dot for both "not in Exact" and "not checked yet" turned every fresh list into a
 * screen full of alarm, while that difference is exactly what you are looking at.
 */
const exactCell = (status?: SyncStatusSummary) => {
    if (!status) {
        return (
            <span
                className="log-status log-status--unknown"
                title="Nog niet nagekeken"
            >
                &ndash;
            </span>
        );
    }

    return (
        <span
            className={
                status.syncedToExact
                    ? 'log-status log-status--ok'
                    : 'log-status log-status--no'
            }
            title={status.summary}
        >
            {status.syncedToExact ? 'ja' : 'nee'}
        </span>
    );
};

export const DashboardDetail = () => {
    const navigate = useNavigate();
    let { customerID, environment } = useParams();

    const [applied, setApplied] = useState<LogQuery>(() =>
        createInitialQuery(customerID, environment)
    );
    const [pending, setPending] = useState<LogQuery | null>(null);

    const [loaded, setLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [data, setData] = useState<QueueLogMessageDto[]>([]);
    const [clientEnvironment, setClientEnvironment] = useState<
        EnvironmentResponseDto[]
    >([]);
    const [latestRun, setLatestRun] = useState<ScenarioRunSummary | undefined>(
        undefined
    );
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const cancelSource = useRef<CancelTokenSource | null>(null);
    const cancelButton = useRef<HTMLButtonElement | null>(null);

    /** What the filter controls show: the user's pick, otherwise the current query. */
    const shown = pending ?? applied;

    /*
        Who is looking, and therefore how much of this page there is.

        Without the admin role this screen is the error log and nothing else. The panels
        around it read the queues, the Exact connection and the traces of a live environment,
        and the buttons in them put messages back on queues - which is why the API refuses
        all of that to a customer account as well. Hiding them is the courtesy; the refusal
        is the rule.
    */
    const {
        isAdmin,
        status: identityStatus,
        error: identityError,
        reload: reloadIdentity,
    } = useIdentity();

    /*
        The environment the admin-only panels watch, or nothing at all.

        Every one of these hooks does nothing without a customer, so passing undefined is
        enough to keep both SignalR hubs closed and every poll unsent. That matters beyond
        tidiness: they would be calls the API answers with 401, over and over, for a panel
        that is not even on screen. Hooks cannot be called conditionally, their arguments can.
    */
    const watchedCustomer = isAdmin ? customerID : undefined;
    const watchedEnvironment = isAdmin ? environment : undefined;

    // Not scoped: the error log leans on this for the "In Exact" column, which is a read
    // every account is allowed. The requeue half of it stays behind the admin check below.
    const requeue = useRequeue(customerID, environment);
    const monitor = useQueueMonitor(watchedCustomer, watchedEnvironment);
    const exact = useExactConnection(watchedCustomer, watchedEnvironment);
    const health = useHealth(watchedCustomer, watchedEnvironment);
    const workerActivity = useWorkerActivity(watchedCustomer, watchedEnvironment);
    const trace = useTrace(watchedCustomer, watchedEnvironment);
    const replayMonitor = useReplay(watchedCustomer, watchedEnvironment);
    const reprocess = useReprocess(watchedCustomer, watchedEnvironment);
    const dossier = useDossier(watchedCustomer, watchedEnvironment);
    const dailyReport = useDailyReport(watchedCustomer, watchedEnvironment);
    const [reportOpen, setReportOpen] = useState<boolean>(false);

    // The dossier keeps its own open state: it is opened from rows and traces far more often
    // than from its own header, and that is the only thing that ever opens it.
    const [dossierOpen, setDossierOpen] = useState<boolean>(false);
    const dossierRef = useRef<HTMLDivElement | null>(null);

    // Settled rows hidden by default: the complaint this answers is a log that keeps filling
    // up with things nobody has to act on any more. The summary says how many are hidden.
    const [hideSettled, setHideSettled] = useState<boolean>(true);
    const [activeCode, setActiveCode] = useState<string | null>(null);

    /** Which offered action is running, as "<finding code>:<action kind>". */
    const [busyAction, setBusyAction] = useState<string | null>(null);

    const [openSections, setOpenSections] = useState<Set<SectionKey>>(
        () => new Set(INITIAL_OPEN)
    );

    const isOpen = (key: SectionKey) => openSections.has(key);

    const toggleSection = useCallback((key: SectionKey) => {
        setOpenSections((current) => {
            const next = new Set(current);

            if (!next.delete(key)) {
                next.add(key);
            }

            return next;
        });
    }, []);

    /**
     * Unfolds a section, for when something elsewhere on the page has to be shown inside
     * it.
     *
     * Every jump on this page - a row that opens a trace, a finding that points at a queue,
     * a replay that reports back - lands in a section that may well be folded up, and
     * scrolling somebody to a closed section is worse than not scrolling at all.
     */
    const revealSection = useCallback((key: SectionKey) => {
        setOpenSections((current) =>
            current.has(key) ? current : new Set(current).add(key)
        );
    }, []);

    /** A queue the diagnosis wants opened in the queue panel. */
    const [focusQueue, setFocusQueue] = useState<string | null>(null);

    /** The error log, so an action can put the reader in front of it. */
    const logsRef = useRef<HTMLElement | null>(null);

    /** The trace panel, so a row in the table can put the reader in front of that. */
    const traceRef = useRef<HTMLDivElement | null>(null);

    /** The replay panel, where a replay started from the trace reports what it did. */
    const replayRef = useRef<HTMLDivElement | null>(null);

    const uniqueObjectTypes = useMemo(() => {
        const typesOptions: IOptionItem[] = [];

        Object.keys(ObjectType).forEach((x) => {
            typesOptions.push({ label: x, value: x });
        });

        return typesOptions;
    }, []);

    const axios = useAxiosSwagger();

    // Keeps the current query out of the dependency list of the effect below: that effect
    // is about the route, not about the filter, and would otherwise retrigger itself after
    // every successful round.
    const appliedRef = useRef(applied);

    useEffect(() => {
        appliedRef.current = applied;
    }, [applied]);

    /**
     * Starts a round: on mount, and after that whenever you navigate to another customer
     * or environment. Same period and same object type, different list.
     *
     * The previous rows do get cleared. For a new period the old rows are what you return
     * to when you cancel, but for another environment they belong under a different
     * heading - there is nothing worth keeping then.
     */
    useEffect(() => {
        setData([]);
        setSelected(new Set());
        setLoaded(false);
        setLoadError(null);
        setPending({
            ...appliedRef.current,
            customer: customerID as string,
            environment: environment as string,
        });
    }, [customerID, environment]);

    /**
     * A new axios instance means a new token.
     *
     * This screen is already mounted before `getAccessTokenSilently` resolves, so the first
     * round may have gone out without a valid header. If that round is still running the
     * effect below picks up the swap by itself; if it already failed on a 401, this
     * restarts it.
     */
    useEffect(() => {
        setPending((current) => current ?? { ...appliedRef.current });
    }, [axios]);

    useEffect(() => {
        if (!axios || !isComplete(pending)) {
            return;
        }

        // Past the guard this is a ReadyQuery; from here on we work with that copy so the
        // closure does not fall back to the wider state version.
        const query = pending;
        const source = axiosLib.CancelToken.source();

        cancelSource.current = source;
        setLoading(true);

        (async () => {
            try {
                const client = new QueueLogClient(undefined, axios);
                const response = query.objectType
                    ? await client.range2(
                          query.customer,
                          query.environment,
                          query.startDate,
                          query.endDate,
                          query.objectType as ObjectType,
                          source.token
                      )
                    : await client.range(
                          query.customer,
                          query.environment,
                          query.startDate,
                          query.endDate,
                          source.token
                      );

                setData(response);
                setSelected(new Set());
                setPage(0);
                setApplied(query);
                setPending(null);
                setLoadError(null);
                setLoaded(true);
            } catch (error) {
                // A cancellation is not an error: the user hit Cancel themselves, or picked
                // another period already. Data and filter stay exactly as they were, and no
                // message appears.
                if (axiosLib.isCancel(error)) {
                    return;
                }

                setLoadError(describeError(error));
                setPending(null);
                setLoaded(true);
            } finally {
                // Only clean up if this is still the running round: an aborted round must
                // not tear down the overlay belonging to its successor.
                if (cancelSource.current === source) {
                    cancelSource.current = null;
                    setLoading(false);
                }
            }
        })();

        return () => source.cancel();
    }, [axios, pending]);

    useEffect(() => {
        if (axios) {
            (async () => {
                try {
                    const client = new EnvironmentClient(undefined, axios);
                    const response = await client.environmentAll(
                        customerID as string
                    );

                    setClientEnvironment(response);
                } catch (error) {
                    console.log(error);
                }
            })();
        }
    }, [customerID, axios]);

    // Only the newest run, and only as an indicator: the run list itself is a page of its own. An environment
    // that has never had a run simply gets no link. Admin only, like the endpoint behind it.
    useEffect(() => {
        if (axios && isAdmin) {
            (async () => {
                try {
                    const client = new ScenarioRunClient(undefined, axios);
                    const response = await client.scenarioRuns(
                        customerID as string,
                        environment as string,
                        1
                    );

                    setLatestRun((response.runs ?? [])[0]);
                } catch (error) {
                    console.log(error);
                }
            })();
        }
    }, [customerID, environment, axios, isAdmin]);

    // The queues requeueing is allowed to write to; the server decides that, because that
    // is where the knowledge about what each queue actually is lives.
    const requeueTargets = useMemo(
        () =>
            (monitor.snapshot?.queues ?? [])
                .filter((q) => q.canRequeueTo)
                .map((q) => q.name),
        [monitor.snapshot]
    );

    const { reconcileQueueName } = requeue;

    useEffect(() => {
        reconcileQueueName(requeueTargets);
    }, [requeueTargets, reconcileQueueName]);

    // Sorting is kept separate from building the rows: this does not have to run again on
    // every checkbox click.
    const sortedData = useMemo(() => {
        // Oldest id on top: the first invoice that went wrong is the one you want first.
        return [...(data || [])].sort((a, b) => {
            const left = Number(a.relationId);
            const right = Number(b.relationId);

            if (!Number.isNaN(left) && !Number.isNaN(right)) {
                return left - right;
            }

            return (a.relationId ?? '').localeCompare(b.relationId ?? '');
        });
    }, [data]);

    /** The rows the summary's filters leave: what the table, the pager and "select all" see. */
    const visibleData = useMemo(
        () =>
            sortedData.filter(
                (x) =>
                    (!hideSettled || !isSettled(x)) &&
                    (!activeCode || codeOf(x) === activeCode)
            ),
        [sortedData, hideSettled, activeCode]
    );

    useEffect(() => {
        setPage(0);
    }, [hideSettled, activeCode]);

    /** Only rows with an objectGuid can be requeued. */
    const requeueable = useMemo(
        () => visibleData.filter((x) => !!x.objectGuid && !!x.relationId),
        [visibleData]
    );

    const byRowKey = useMemo(() => {
        const map = new Map<string, QueueLogMessageDto>();

        requeueable.forEach((x) => map.set(x.rowKey as string, x));

        return map;
    }, [requeueable]);

    const pageCount = Math.max(1, Math.ceil(visibleData.length / pageSize));
    const currentPage = Math.min(page, pageCount - 1);

    const pageRows = useMemo(
        () =>
            visibleData.slice(
                currentPage * pageSize,
                currentPage * pageSize + pageSize
            ),
        [visibleData, currentPage, pageSize]
    );

    /**
     * Check right away whether the visible rows have reached Exact by now.
     *
     * This used to sit behind a button, but then the "In Exact" column stays empty until
     * someone finds that button - and a column that says nothing by default might as well
     * be dropped. It costs one call per page, regardless of the number of rows.
     */
    const { checkStatuses } = requeue;

    useEffect(() => {
        // Not while loading: those would be status calls for rows about to be replaced by
        // another list.
        if (loading) {
            return;
        }

        const items = pageRows
            .filter((x) => !!x.relationId)
            .map((x) => ({
                objectType: x.objectType as string,
                relationId: x.relationId as string,
            }));

        checkStatuses(items);
    }, [pageRows, checkStatuses, loading]);

    /*
        The trace as a link.

        Three query parameters rather than one: an id on its own resolves to several objects
        often enough, and a colleague who is sent "this payment" should land on that payment
        rather than on a list to pick from again. The type and the relation id also reach the
        objects that have no relation row at all - which is the case a trace is needed for
        most, and the one an id alone cannot get to.
    */
    const [searchParams, setSearchParams] = useSearchParams();

    const { open: openTrace } = trace;

    const showTrace = useCallback(
        (id: string, objectType?: string, relationId?: string) => {
            openTrace({ id, objectType, relationId });
            revealSection('trace');

            traceRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        },
        [openTrace, revealSection]
    );

    /**
     * Opens the dossier of an invoice and brings it into view, with Chargebee and Exact read live: from the
     * logs alone a dossier shows what the connector saw then, not where the invoice stands now.
     */
    const showDossier = useCallback(
        (id: string): boolean => {
            if (!dossier.open({ id, chargebee: true, exact: true })) {
                return false;
            }

            setDossierOpen(true);

            dossierRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });

            return true;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dossier.open]
    );

    /** A link that was pasted in opens the trace it names, once. */
    const openedFromUrl = useRef<boolean>(false);

    useEffect(() => {
        if (openedFromUrl.current) {
            return;
        }

        const dossierId = searchParams.get('dossier');

        // Only once it could actually start: on the first render the API client is not
        // there yet, and marking the link as handled then would drop it for good.
        if (dossierId) {
            openedFromUrl.current = showDossier(dossierId);

            return;
        }

        const id = searchParams.get('trace');

        if (!id) {
            return;
        }

        openedFromUrl.current = true;

        openTrace({
            id,
            objectType: searchParams.get('traceType') ?? undefined,
            relationId: searchParams.get('traceId') ?? undefined,
        });

        // A link that names a trace is a link to that trace, so the section it lands in has
        // to be open before the reader arrives.
        revealSection('trace');
    }, [searchParams, openTrace, revealSection, showDossier]);

    /*
        And the other way round: what is on screen ends up in the address bar, so the URL
        can be copied. Replace rather than push - a trace is a view of this page, not a step
        in the history somebody should have to press back through five times.
    */
    const traceRequest = trace.request;

    useEffect(() => {
        // On the very first render of a pasted link there is a trace in the URL and not yet
        // one on screen, and writing that state back out would wipe the link before the
        // effect above has read it.
        if (
            !traceRequest &&
            !openedFromUrl.current &&
            searchParams.has('trace')
        ) {
            return;
        }

        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);

                if (!traceRequest) {
                    next.delete('trace');
                    next.delete('traceType');
                    next.delete('traceId');

                    return next;
                }

                next.set(
                    'trace',
                    traceRequest.id ?? traceRequest.relationId ?? ''
                );

                if (traceRequest.objectType) {
                    next.set('traceType', traceRequest.objectType);
                } else {
                    next.delete('traceType');
                }

                if (traceRequest.relationId) {
                    next.set('traceId', traceRequest.relationId);
                } else {
                    next.delete('traceId');
                }

                return next;
            },
            { replace: true }
        );
    }, [traceRequest, searchParams, setSearchParams]);

    const toRequeueItem = (x: QueueLogMessageDto): RequeueItem => ({
        objectType: x.objectType as string,
        eventType: x.eventType as string,
        objectGuid: x.objectGuid as string,
        relationId: x.relationId as string,
    });

    const toggleRow = useCallback((rowKey: string) => {
        setSelected((current) => {
            const next = new Set(current);

            if (!next.delete(rowKey)) {
                next.add(rowKey);
            }

            return next;
        });
    }, []);

    // Deliberately on the individual fields and not on the whole requeue object: that is a
    // new object every render, which would rebuild the table rows every render.
    const { queueName, requeue: sendRequeue } = requeue;

    const { run: runReprocess } = reprocess;
    const { check: checkReplay } = replayMonitor;

    const runRequeue = useCallback(
        async (items: RequeueItem[], toQueue?: string | null) => {
            if (items.length === 0) {
                return;
            }

            const target = toQueue || queueName;

            // There is no dry run any more, so this is the only moment left to back out.
            // Even for a single object: on the wrong queue it vanishes without a trace.
            if (
                !window.confirm(
                    items.length === 1
                        ? `Object ${items[0].relationId} opnieuw aanbieden op "${target}"?`
                        : `${items.length} object(en) opnieuw aanbieden op "${target}"?`
                )
            ) {
                return;
            }

            const response = await sendRequeue(items, target);

            if (response && response.queued > 0) {
                setSelected(new Set());
            }
        },
        [queueName, sendRequeue]
    );

    /**
     * Sends a selection that spans two queues, one call per queue.
     *
     * The bar works the split out from the object types; this only confirms it once and
     * then makes the calls. Two calls rather than one because the queue is a property of
     * the request, and a batch that mixes destinations has no single right value for it -
     * which is precisely the mistake that used to be a dropdown.
     */
    const runRequeuePlan = useCallback(
        async (groups: { queue: string; items: RequeueItem[] }[]) => {
            const total = groups.reduce(
                (count, group) => count + group.items.length,
                0
            );

            if (total === 0) {
                return;
            }

            const summary = groups
                .map((group) => `${group.items.length} naar "${group.queue}"`)
                .join(', ');

            if (
                !window.confirm(
                    `${total} object(en) opnieuw aanbieden? ${summary}.`
                )
            ) {
                return;
            }

            let queued = 0;

            for (const group of groups) {
                // Sequential on purpose: two requeues at once would interleave their
                // progress bars and their result cards into one unreadable mess.
                // eslint-disable-next-line no-await-in-loop
                const response = await sendRequeue(group.items, group.queue);

                queued += response?.queued ?? 0;
            }

            if (queued > 0) {
                setSelected(new Set());
            }
        },
        [sendRequeue]
    );

    /**
     * Offers the object on screen again, on the queue the trace worked out it belongs on.
     *
     * Runs through the same confirmation and the same call as a row in the table; the one
     * thing it does not share is the destination. The queue above the error log is a choice
     * a person makes for a whole selection, while the trace knows the type of this one
     * object and therefore which transfer queue has a handler for it. On the other one the
     * handler has no case for the type: the message is removed from the queue and the same
     * function writes the object into the sync log as successfully synchronised, so the
     * mistake leaves no trace at all.
     */
    const requeueTraced = useCallback(
        (subject: TraceSubject, toQueue?: string | null) => {
            if (!subject.objectGuid || !subject.relationId) {
                return;
            }

            void runRequeue(
                [
                    {
                        objectType: subject.objectType,
                        eventType: subject.eventType,
                        objectGuid: subject.objectGuid,
                        relationId: subject.relationId,
                    },
                ],
                toQueue
            );
        },
        [runRequeue]
    );

    /**
     * Offers the raw webhook of the object on screen to the receive path again.
     *
     * The other half of the chain, and therefore the other queue. The requeue above writes a
     * message naming a stored copy of the connector's own model; this one writes the message
     * naming the body the sending system delivered, which is all that exists when the receive
     * function refused the event and swallowed the refusal. Confirmed the same way, because it
     * is the same kind of act: one message on one queue, in a live environment.
     */
    const reprocessTraced = useCallback(
        (subject: TraceSubject) => {
            if (!subject.inboundEventType || !subject.inboundEventId) {
                return;
            }

            if (
                !window.confirm(
                    `De opgeslagen webhook "${subject.inboundEventType},${subject.inboundEventId}" opnieuw laten verwerken?`
                )
            ) {
                return;
            }

            void runReprocess(subject.inboundEventType, subject.inboundEventId);
        },
        [runReprocess]
    );

    /**
     * Fetches the object on screen out of Chargebee and shows what offering it would do.
     *
     * Reads only. The way back that does not need anything the connector kept, so it is the one
     * that is left when the stored body is gone as well - and the one that brings the object's
     * current state rather than the state the missed webhook carried. What Chargebee has lands
     * in the replay panel, next to the button that offers it to the connector: that stays a
     * separate press, after the reader has seen what would go.
     */
    const replayTraced = useCallback(
        (subject: TraceSubject) => {
            const kind = REPLAY_KIND_OF[subject.objectType];

            if (!kind || !subject.relationId) {
                return;
            }

            revealSection('replay');

            void checkReplay(kind, [subject.relationId]).then(() =>
                replayRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
            );
        },
        [checkReplay, revealSection]
    );

    const { setQueueName } = requeue;

    /**
     * Carries out one of the actions the diagnosis offered.
     *
     * Nothing here decides anything. The panel worked out what is wrong and what would help;
     * this puts the reader in front of it with the right things already selected, or performs
     * the one step that is safe to perform on its own - renewing a token that has expired
     * costs nothing and cannot make anything worse.
     *
     * "Offer again now" deliberately stops one click short. It could requeue every failed
     * object in the visible period by itself, and that is exactly why it does not: a button
     * that silently resubmits a hundred and forty-five objects is not a button anybody should
     * press without seeing the list first. So it scrolls to the log, sets the destination
     * queue and ticks the rows, leaving the last press where it belongs.
     */
    const handleFindingAction = useCallback(
        async (finding: Finding, action: FindingAction) => {
            const key = `${finding.code}:${action.kind}`;

            switch (action.kind) {
                case 'RefreshExactToken':
                    setBusyAction(key);

                    try {
                        await exact.refresh();
                        health.reload();
                    } finally {
                        setBusyAction(null);
                    }

                    break;

                case 'ReconnectExact':
                    exact.reconnect();

                    break;

                case 'OpenErrorLog':
                    revealSection('logs');

                    logsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });

                    break;

                case 'RequeuePoison':
                    // The poison panel lives inside the opened queue card, next to the
                    // messages it would move.
                    revealSection('queues');
                    setFocusQueue(action.queue ?? null);

                    break;

                case 'OfferAgainNow':
                    if (action.queue) {
                        setQueueName(action.queue);
                    }

                    setSelected(
                        new Set(requeueable.map((x) => x.rowKey as string))
                    );

                    revealSection('logs');

                    logsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });

                    break;

                case 'OpenWorkerLog':
                    // Straight into the app's own telemetry: which invocations ran, what
                    // the host logged, and which exception accounts for the failures. The
                    // one question storage can never answer is whether the host started at
                    // all, and that is what this opens on. It reports inside the queue
                    // section, under the badge that was clicked.
                    if (finding.worker) {
                        workerActivity.open(finding.worker);
                    }

                    revealSection('queues');

                    break;

                default:
                    break;
            }
        },
        [
            exact,
            health,
            requeueable,
            revealSection,
            setQueueName,
            workerActivity,
        ]
    );

    const showParent = useMemo(
        () => optionalColumn(pageRows, (x) => x.additionalInfo1),
        [pageRows]
    );

    const showRelated = useMemo(
        () => optionalColumn(pageRows, (x) => x.additionalInfo2),
        [pageRows]
    );

    const tableHeader = useMemo(
        () => [
            // The checkbox in front and the two buttons at the end are there for an account
            // that may act on a row. Left out rather than disabled: a column of greyed-out
            // buttons is an invitation to ask why, every single row.
            ...(isAdmin ? [''] : []),
            'Type',
            'Id',
            'Actie',
            ...(showParent ? ['Parent'] : []),
            ...(showRelated ? ['Gerelateerd'] : []),
            'Melding',
            'Datum',
            'In Exact',
            ...(isAdmin ? ['', ''] : []),
        ],
        [showParent, showRelated, isAdmin]
    );

    const formattedTableData = useMemo(() => {
        const tData: ITableRow[] = [];

        pageRows.forEach((x) => {
            const rowKey = x.rowKey as string;
            const canRequeue = !!x.objectGuid && !!x.relationId;
            const status =
                requeue.statuses[statusKey(x.objectType, x.relationId)];
            const timestamp = x.timestamp ?? '';
            const message = x.errorFunctionalMessage || x.errorMessage || '';
            const text = x.errorText || x.errorMessage || message;

            const row: ITableRow = {
                id: rowKey,
                values: [
                    ...(isAdmin
                        ? [
                              <input
                                  type="checkbox"
                                  checked={selected.has(rowKey)}
                                  disabled={!canRequeue}
                                  onChange={() => toggleRow(rowKey)}
                                  aria-label={`Selecteer ${x.relationId}`}
                                  title={
                                      canRequeue
                                          ? undefined
                                          : 'Geen objectGuid in de logregel; deze kan niet opnieuw worden aangeboden'
                                  }
                              />,
                          ]
                        : []),
                    <span className="c-type" title={x.objectType}>
                        {x.objectType}
                    </span>,
                    <span className="c-id" title={x.relationId}>
                        {x.relationId}
                    </span>,
                    <span className="c-action">{x.eventType}</span>,
                    ...(showParent
                        ? [
                              <span className="c-ref" title={x.additionalInfo1}>
                                  {x.additionalInfo1}
                              </span>,
                          ]
                        : []),
                    ...(showRelated
                        ? [
                              <span className="c-ref" title={x.additionalInfo2}>
                                  {x.additionalInfo2}
                              </span>,
                          ]
                        : []),
                    // Two lines tall with the full text in the tooltip: these messages run
                    // three sentences long and turned every row into half a screen.
                    // The reading first, when there is one; the connector's own words stay
                    // underneath, because they are what a developer searches the code for.
                    x.interpretation ? (
                        <div className="c-reading">
                            <InterpretationNote
                                interpretation={x.interpretation}
                                compact
                            />
                            <span className="interpretation__raw" title={text}>
                                {text}
                            </span>
                        </div>
                    ) : (
                        <span className="c-message" title={text}>
                            {text}
                        </span>
                    ),
                    <span className="c-date">
                        {typeof timestamp === 'string'
                            ? timestamp.substring(0, timestamp.indexOf('T'))
                            : ''}
                    </span>,
                    exactCell(status),
                    /*
                        Offering a row again, and following it. Both admin only, and the
                        second one for a reason of its own: "Volgen" opens the trace panel,
                        which is not on the page at all without the role.
                    */
                    ...(isAdmin
                        ? [
                              canRequeue ? (
                                  <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      disabled={requeue.busy}
                                      onClick={() =>
                                          runRequeue([toRequeueItem(x)])
                                      }
                                  >
                                      Opnieuw
                                  </button>
                              ) : null,
                              // Where the question usually starts: a row in this list, and
                              // somebody wanting to know what happened to that one thing.
                              <div className="c-links">
                                  <button
                                      type="button"
                                      className="requeue-link"
                                      onClick={() =>
                                          showTrace(
                                              x.relationId as string,
                                              x.objectType as string,
                                              x.relationId as string
                                          )
                                      }
                                      title="Laat zien wat er met dit object is gebeurd"
                                  >
                                      Volgen
                                  </button>
                                  {x.relationId && (
                                      <button
                                          type="button"
                                          className="requeue-link"
                                          onClick={() =>
                                              showDossier(
                                                  x.relationId as string
                                              )
                                          }
                                          title="Dit object met alles eromheen - factuur, betalingen, storneringen, klant - op één tijdlijn"
                                      >
                                          Dossier
                                      </button>
                                  )}
                              </div>,
                          ]
                        : []),
                ],
            };

            tData.push(row);
        });

        return tData;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pageRows,
        selected,
        requeue.statuses,
        requeue.busy,
        showParent,
        showRelated,
        isAdmin,
        toggleRow,
        runRequeue,
        showTrace,
    ]);

    const uniqueEnvironments = useMemo(() => {
        const options: IOptionItem[] = [];

        const list: EnvironmentResponseDto[] = clientEnvironment || [];
        list.forEach((x) => {
            options.push({ label: x.environment!, value: x.environment! });
        });

        return options;
    }, [clientEnvironment]);

    const allSelected =
        requeueable.length > 0 && selected.size === requeueable.length;

    const toggleAll = () => {
        setSelected(
            allSelected
                ? new Set()
                : new Set(requeueable.map((x) => x.rowKey as string))
        );
    };

    /** The selected rows as requeue items, without scanning the whole list. */
    const selectedItems = () => {
        const items: RequeueItem[] = [];

        selected.forEach((rowKey) => {
            const row = byRowKey.get(rowKey);

            if (row) {
                items.push(toRequeueItem(row));
            }
        });

        return items;
    };

    /** Refetch the list; rows that succeeded disappear from it automatically. */
    const handleReloadList = () => {
        // A new object with the same values: enough to make the load effect run again,
        // without a separate counter that has nothing to do with the query.
        setPending({ ...applied });
    };

    const handleEnvironmentSelect = (selectedValue: string) => {
        if (!selectedValue) {
            return;
        }
        navigate(`/dashboard/${customerID}/${selectedValue}`);
    };

    const handleDateChange = (range: any) => {
        const [startDate, endDate] = range;
        setPending({ ...shown, startDate, endDate });
    };

    const handleObjectTypeChange = (selectedValue: string) => {
        setPending({ ...shown, objectType: selectedValue as ObjectType | '' });
    };

    /** Back to the first page: page seven of a list that now has three makes no sense. */
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(0);
    };

    /**
     * Stop the fetch and leave everything as it was.
     *
     * The filter needs no restoring: the controls bind to `pending`, so throwing it away
     * puts them back on the values that belong to the visible rows.
     */
    const cancelLoad = useCallback(() => {
        cancelSource.current?.cancel('Ophalen geannuleerd');
        cancelSource.current = null;
        setPending(null);
        setLoading(false);
    }, []);

    // The overlay covers the filters too, so the button is the only way out. That is why
    // it takes focus, and why Escape listens along for anyone not using a mouse. When the
    // overlay goes away focus returns to the filter it came from - otherwise a keyboard
    // user ends up on <body> afterwards, with no idea where they were.
    useEffect(() => {
        if (!loading) {
            return;
        }

        const previous = document.activeElement as HTMLElement | null;

        cancelButton.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                cancelLoad();
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);

            if (previous && document.contains(previous)) {
                previous.focus();
            }
        };
    }, [loading, cancelLoad]);

    const loadingLabel = useMemo(() => {
        const period = `van ${dayLabel(shown.startDate)} tot ${dayLabel(
            shown.endDate
        )}`;

        return shown.objectType
            ? `Fouten op ${shown.objectType} ${period} ophalen...`
            : `Fouten ${period} ophalen...`;
    }, [shown]);

    // The bar at the top, for what was asked for here; the health polls by itself and only
    // counts the first time.
    useBusy(loading, loadingLabel.replace(/\.+$/, ''));
    useBusy(dossier.loading, 'Dossier opbouwen');
    useBusy(dailyReport.loading, 'Dagoverzicht ophalen');
    useBusy(health.loading && !health.health, 'Vaststellen wat er in deze omgeving gebeurt');

    /**
     * The state of the table itself.
     *
     * Loading is no longer part of this value: while fetching, the previous rows stay put,
     * because the overlay on top only makes sense when there is something underneath to
     * return to. Only a failed round with no data empties the table.
     */
    const tableState: loadingStateType = !loaded
        ? 'init'
        : loadError && sortedData.length === 0
        ? 'error'
        : 'loaded';

    const firstOnPage =
        visibleData.length === 0 ? 0 : currentPage * pageSize + 1;
    const lastOnPage = Math.min(
        (currentPage + 1) * pageSize,
        visibleData.length
    );

    return (
        <main className="dashboard-detail">
            <header className="dashboard-detail__header">
                <Breadcrumbs items={environmentCrumbs(customerID, environment)} />

                <div className="dashboard-detail__heading">
                    <h2 className="dashboard-detail__title">
                        {customerID}
                        <span className="dashboard-detail__env">
                            {environment}
                        </span>
                    </h2>

                    {isAdmin && (
                        <div className="dashboard-detail__actions">
                            <ScenarioTestsButton
                                to={scenarioTestsPath(customerID, environment)}
                                latestRun={latestRun}
                            />
                        </div>
                    )}
                </div>
            </header>

            {/*
                A failed roles call leaves the page looking exactly like a customer account,
                which for an admin is a page with five panels missing and no reason given.
                So it says so, and offers the round again.
            */}
            {identityStatus === 'error' && (
                <p className="dashboard-detail__error" role="alert">
                    Kon niet vaststellen welke rechten je hebt, dus dit scherm
                    toont alleen het foutenlogboek. {identityError}{' '}
                    <button
                        type="button"
                        className="requeue-link"
                        onClick={reloadIdentity}
                    >
                        Opnieuw proberen
                    </button>
                </p>
            )}

            {isAdmin && (
                <>
                    {SHOW_HEALTH && (
                        <HealthPanel
                            health={health.health}
                            loading={health.loading}
                            error={health.error}
                            onReload={health.reload}
                            onAction={handleFindingAction}
                            busyAction={busyAction}
                            activity={workerActivity}
                        />
                    )}

                    <ExactConnectionPanel
                        monitor={exact}
                        open={isOpen('exact')}
                        onToggle={() => toggleSection('exact')}
                    />

                    <QueueMonitorPanel
                        snapshot={monitor.snapshot}
                        connection={monitor.connection}
                        error={monitor.error}
                        onRefresh={monitor.refresh}
                        customer={customerID}
                        environment={environment}
                        focusQueue={focusQueue}
                        onFocusHandled={() => setFocusQueue(null)}
                        fleet={health.health?.workers}
                        findings={health.health?.findings}
                        openWorker={workerActivity.appKey}
                        onOpenWorker={workerActivity.open}
                        open={isOpen('queues')}
                        onToggle={() => toggleSection('queues')}
                    />

                    {/*
                        What one app actually did, under the badges it is opened from.

                        It used to sit in the diagnosis panel, which is where the finding
                        that asks for it lives - but with that panel out of the way a click
                        on a badge had nowhere to report to. Here it is next to the pipeline,
                        which is where the badges are.
                    */}
                    <WorkerActivityPanel monitor={workerActivity} />

                    <DailyReportPanel
                        monitor={dailyReport}
                        open={reportOpen}
                        onToggle={() => setReportOpen(!reportOpen)}
                        onRequeue={(items) => runRequeue(items)}
                        requeueBusy={requeue.busy}
                        onDossier={showDossier}
                    />

                    <TrackingPanel
                        tracked={requeue.trackedSummary}
                        items={requeue.tracked}
                        polling={requeue.polling}
                        checking={requeue.checking}
                        onCheck={requeue.checkTracked}
                        onStopPolling={requeue.stopPolling}
                        onForget={requeue.forgetTracked}
                        onTrace={showTrace}
                        open={isOpen('tracking')}
                        onToggle={() => toggleSection('tracking')}
                    >
                        {/*
                            What the last requeue reported. It used to stand in the error
                            log, next to the button that produced it, which split one story
                            over two sections: the objects just offered again down there, the
                            list that follows those same objects up here.
                        */}
                        <RequeueResult
                            result={requeue.result}
                            statuses={requeue.statuses}
                            error={requeue.error}
                            onReloadList={handleReloadList}
                            onDismiss={requeue.dismissResult}
                        />
                    </TrackingPanel>

                    <div ref={traceRef}>
                        <TracePanel
                            monitor={trace}
                            onRequeue={requeueTraced}
                            requeueBusy={requeue.busy}
                            onReprocess={reprocessTraced}
                            reprocessBusy={reprocess.busy}
                            reprocessResult={reprocess.result}
                            reprocessError={reprocess.error}
                            onDismissReprocess={reprocess.dismiss}
                            onReplay={replayTraced}
                            replayBusy={replayMonitor.busy}
                            // Same as the health panel does with the same action: the poison
                            // panel lives inside the opened queue card, next to the messages
                            // it moves.
                            onOpenPoison={(queue) => {
                                revealSection('queues');
                                setFocusQueue(queue);
                            }}
                            onOpenDossier={showDossier}
                            open={isOpen('trace')}
                            onToggle={() => toggleSection('trace')}
                        />
                    </div>

                    {/* Next to the trace: the trace follows one object, this an invoice and all around it. */}
                    <div ref={dossierRef}>
                        <DossierPanel
                            monitor={dossier}
                            open={dossierOpen}
                            onToggle={() => setDossierOpen(!dossierOpen)}
                            onTrace={showTrace}
                        />
                    </div>

                    {/*
                        Under the trace, because that is where the need for it turns up:
                        following an invoice that is nowhere ends in "de connector heeft dit
                        object nooit gezien", and this is the only thing on the page that can
                        do something about that.
                    */}
                    <div ref={replayRef}>
                        <ReplayPanel
                            monitor={replayMonitor}
                            open={isOpen('replay')}
                            onToggle={() => toggleSection('replay')}
                        />
                    </div>
                </>
            )}

            <CollapsibleSection
                id="error-log"
                className="dashboard-detail__logs"
                title="Foutenlogboek"
                open={isOpen('logs')}
                onToggle={() => toggleSection('logs')}
                innerRef={logsRef}
                busy={loading}
                status={
                    loaded && !loadError
                        ? `${sortedData.length} regel(s) in de gekozen periode`
                        : undefined
                }
                tools={
                    <div className="dashboard-detail__filters">
                        <Select
                            initialLabel="Omgeving"
                            options={uniqueEnvironments}
                            value={environment}
                            callBack={handleEnvironmentSelect}
                            ariaLabel="Omgeving"
                            customClassName="filter"
                        />

                        <Select
                            initialLabel="Alle types"
                            options={uniqueObjectTypes}
                            value={shown.objectType}
                            callBack={handleObjectTypeChange}
                            ariaLabel="Objecttype"
                            customClassName="filter"
                        />

                        <div className="relative">
                            <img
                                src={datePicker}
                                className="date-picker"
                                alt=""
                            />
                            <ReactDatePicker
                                selectsRange={true}
                                startDate={shown.startDate}
                                endDate={shown.endDate}
                                dateFormat={'dd/MM/yyyy'}
                                onChange={handleDateChange}
                                className="form-control filter filter--date multi"
                                placeholderText="Periode"
                                isClearable={shown.startDate !== null}
                            />
                        </div>

                        <button
                            type="button"
                            className="dashboard-detail__reload"
                            onClick={handleReloadList}
                            title="Lijst opnieuw ophalen"
                        >
                            <FontAwesomeIcon icon="rotate" />
                            <span className="visually-hidden">
                                Lijst opnieuw ophalen
                            </span>
                        </button>
                    </div>
                }
            >
                {loadError && (
                    <p className="dashboard-detail__error" role="alert">
                        Ophalen mislukt: {loadError}
                    </p>
                )}

                {/* The bar is nothing but requeue controls, so it goes with them. */}
                {isAdmin && (
                    <RequeueBar
                        selectedCount={selected.size}
                        selectableCount={requeueable.length}
                        blockedCount={data.length - requeueable.length}
                        selectedItems={selectedItems()}
                        targets={requeueTargets}
                        ambiguousQueue={requeue.queueName}
                        onAmbiguousQueueChange={requeue.setQueueName}
                        busy={requeue.busy}
                        progress={requeue.progress}
                        onToggleAll={toggleAll}
                        onRequeue={runRequeuePlan}
                    />
                )}

                {tableState === 'loaded' && (
                    <ErrorSummary
                        rows={sortedData}
                        hideSettled={hideSettled}
                        onHideSettledChange={setHideSettled}
                        activeCode={activeCode}
                        onActiveCodeChange={setActiveCode}
                        issuesLink={
                            isAdmin
                                ? `/issues?customer=${encodeURIComponent(customerID ?? '')}&environment=${encodeURIComponent(environment ?? '')}&status=all&days=90`
                                : undefined
                        }
                    />
                )}

                {/*
                    Rendered as soon as there are rows, one page or a hundred: a bar that
                    only appears once paging kicks in makes the count jump the moment you
                    put a filter on.
                */}
                {tableState === 'loaded' && visibleData.length > 0 && (
                    <TablePager
                        className="table-pager--top"
                        firstOnPage={firstOnPage}
                        lastOnPage={lastOnPage}
                        total={visibleData.length}
                        page={currentPage}
                        pageCount={pageCount}
                        pageSize={pageSize}
                        pageSizes={PAGE_SIZE_OPTIONS}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        rowNoun="berichten"
                        note="gefilterd"
                        hint={LIST_SCOPE_HINT}
                    />
                )}

                <TableSimple
                    header={tableHeader}
                    rows={formattedTableData}
                    styleClasses="arl-table__logs"
                    dataLoaded={loaded}
                    loadingState={tableState}
                />

                {/* Only worth repeating underneath once there is a next page to go to. */}
                {tableState === 'loaded' && pageCount > 1 && (
                    <TablePager
                        className="table-pager--bottom"
                        firstOnPage={firstOnPage}
                        lastOnPage={lastOnPage}
                        total={visibleData.length}
                        page={currentPage}
                        pageCount={pageCount}
                        pageSize={pageSize}
                        pageSizes={PAGE_SIZE_OPTIONS}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        rowNoun="berichten"
                    />
                )}

                {loading && (
                    <div className="dashboard-detail__loading">
                        <div
                            className="dashboard-detail__loading-box"
                            role="status"
                        >
                            <FontAwesomeIcon
                                icon="spinner"
                                spin
                                className="dashboard-detail__loading-spinner"
                            />

                            <p className="dashboard-detail__loading-text">
                                {loadingLabel}
                            </p>

                            <button
                                type="button"
                                ref={cancelButton}
                                className="btn btn-sm btn-outline-secondary"
                                onClick={cancelLoad}
                            >
                                Annuleren
                            </button>
                        </div>
                    </div>
                )}
            </CollapsibleSection>
        </main>
    );
};
